import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import ContentBlock from '@/components-latest/primitives/ContentBlock'
import Store from '@/components-latest/primitives/Icons/Store'
import Spinner, { LoadingSpinner } from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import RevenueBar from '@/components-latest/ui/PerformancePage/RevenueBar'
import { IRevenuePerformanceData } from '@/ducks/performance/performance.schema'
import { selectCurrencyCode } from '@/ducks/store/selectors/store.selectors'
import { formatPrice } from '@/utils/currency.utils'

import Styles from './RevenuePerformance.module.scss'

type Props = {
  data?: IRevenuePerformanceData[]
  isLoading?: boolean
  timeframe: 'hours' | 'days'
  multiple?: boolean
}

const RevenuePerformance = React.memo(
  ({ isLoading, data = [], timeframe = 'hours', multiple = false }: Props) => {
    const { t } = useTranslation()
    const currency = useSelector(selectCurrencyCode) || 'DZD'

    if (isLoading) {
      return (
        <ContentBlock>
          <div className={Styles.spinner}>
            <LoadingSpinner 
              containerType="centered" 
              text="Loading revenue data..."
            />
          </div>
        </ContentBlock>
      )
    }

    if (!data.length) {
      return (
        <ContentBlock>
          <Text asChild>
            <p>{t('performance.revenue.no-data')}</p>
          </Text>
        </ContentBlock>
      )
    }

    return (
      <ContentBlock>
        <Text variant="nextTitle-2" weight="extraBold" asChild>
          <p>{t('performance.revenue.title')}</p>
        </Text>

        {data.map((store, idx) => {
          const info = [
            {
              id: 'profit',
              value: formatPrice(
                parseFloat(store?.netEarning || '0'),
                currency,
                {
                  signDisplay: 'never',
                  maximumFractionDigits: 0,
                  minimumFractionDigits: 0
                }
              ),
              text: t('performance.revenue.net-profit')
            },
            {
              id: 'gross',
              value: formatPrice(
                parseFloat(store?.grossEarning || '0'),
                currency,
                {
                  signDisplay: 'always',
                  maximumFractionDigits: 0,
                  minimumFractionDigits: 0
                }
              ),
              text: t('performance.revenue.gross-earning')
            },
            {
              id: 'comission',
              value: `-${formatPrice(
                parseFloat(store?.comission || '0'),
                currency,
                {
                  signDisplay: 'never',
                  maximumFractionDigits: 0,
                  minimumFractionDigits: 0
                }
              )}`,
              text: t('performance.revenue.yassir-comission')
            }
          ]

          return (
            <div key={idx} className={Styles.content}>
              <div className={Styles.info}>
                {multiple && store?.area && (
                  <div className={Styles.infoItem}>
                    <div className={Styles.row}>
                      <Store />
                      <Text
                        variant="subtitle"
                        className={Styles.comission}
                        asChild>
                        <p>{store?.area}</p>
                      </Text>
                    </div>
                  </div>
                )}
                {info.map(({ id, value, text }) => (
                  <div key={id} className={Styles.infoItem}>
                    <Text
                      variant="nextTitle-1"
                      weight="extraBold"
                      className={Styles[id]}
                      asChild>
                      <p>{value}</p>
                    </Text>
                    <Text
                      className={Styles.infoText}
                      variant="nextBody"
                      asChild>
                      <p>{text}</p>
                    </Text>
                  </div>
                ))}
              </div>
              {!multiple && (
                <div className={Styles.chart}>
                  {store?.revenue && store.revenue.length > 0 ? (
                    <>
                      <RevenueBar
                        revenues={store.revenue}
                        timeframe={timeframe}
                      />
                      <Text variant="nextSubtitle" className={Styles.legend}>
                        {t('performance.revenue.net-profit')}
                      </Text>
                    </>
                  ) : (
                    <Text asChild>
                      <p>{t('performance.revenue.no-data')}</p>
                    </Text>
                  )}
                </div>
              )}
            </div>
          )
        })}

        {multiple && (
          <div className={Styles.chart}>
            <RevenueBar
              revenues={data.map(store => ({
                area: store?.area,
                revenue: store?.revenue || []
              }))}
              timeframe={timeframe}
              multiple
            />
          </div>
        )}
      </ContentBlock>
    )
  }
)

RevenuePerformance.displayName = 'RevenuePerformance'
export default RevenuePerformance
